/** Prepare nearby posters and play visible videos; motion remains in CSS. */
class LKScrolledCards extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.prepareImages();
    this.videos = [...this.querySelectorAll('[data-scrolled-cards-video]')];
    const signal = this.controller.signal;
    this.observer = new IntersectionObserver((entries) => {
      for (const { target: video, isIntersecting } of entries) {
        video.toggleAttribute('data-in-view', isIntersecting);
        this.updateVideo(video);
      }
    }, { threshold: .1 });
    for (const video of this.videos) {
      video.muted = true;
      const ready = () => video.closest('.scrolled-cards__video-wrap')?.classList.add('is-video-ready');
      video.addEventListener('loadeddata', ready, { once: true, signal });
      if (video.readyState >= 2) ready();
      this.observer.observe(video);
    }
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => this.videos.forEach((video) => this.updateVideo(video));
    this.motion.addEventListener('change', update, { signal });
    document.addEventListener('visibilitychange', update, { signal });
  }

  prepareImages() {
    const images = [...this.querySelectorAll('img[data-lk-src]')];
    const load = (image) => {
      image.loading = 'eager';
      if (image.dataset.lkSrcset) image.srcset = image.dataset.lkSrcset;
      image.src = image.dataset.lkSrc;
      delete image.dataset.lkSrcset;
      delete image.dataset.lkSrc;
    };
    if (!images.length) return;
    if (!('IntersectionObserver' in window)) {
      images.forEach(load);
      return;
    }
    const wrapper = this.closest('.page-wrapper');
    const root = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : null;
    this.imageObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!this.isConnected || !entry.isIntersecting || !entry.target.dataset.lkSrc) continue;
        load(entry.target);
        this.imageObserver.unobserve(entry.target);
      }
    }, { root, rootMargin: '160px 0px' });
    images.forEach((image) => this.imageObserver.observe(image));
  }

  canPlay(video) {
    return this.isConnected && video.hasAttribute('data-in-view') && !document.hidden && !this.motion?.matches;
  }

  updateVideo(video) {
    if (this.canPlay(video)) {
      const sources = [...video.querySelectorAll('source[data-scrolled-src]')];
      for (const source of sources) {
        source.src = source.dataset.scrolledSrc;
        delete source.dataset.scrolledSrc;
      }
      if (sources.length) video.load();
      video.play()?.then(() => {
        // Autoplay may resolve after a scroll, tab switch or editor removal.
        if (!this.canPlay(video)) video.pause();
      }).catch(() => {});
    } else {
      video.pause();
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.imageObserver?.disconnect();
    this.videos?.forEach((video) => video.pause());
  }
}

if (!customElements.get('lk-scrolled-cards')) customElements.define('lk-scrolled-cards', LKScrolledCards);
